import { cloneElement, createContext, isValidElement, useContext } from 'react';
import { ArrowCounterClockwise, CaretDown, CaretUp, Check, X } from '@phosphor-icons/react';
import { SOURCE_TEST_IDS, sourceWarning } from '@/lib/documentSelection';

export const IncomingDocumentContext = createContext(null);
const LABEL = 'block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-0.5 truncate';

// Stable components keep the focused input mounted while the selected value changes.
// A pending value is display-only: generic autosave must never save it before confirmation.
export const IncomingDocumentField = ({ label, children, span = '' }) => {
  const { source } = useContext(IncomingDocumentContext);
  const testId = isValidElement(children) ? children.props['data-testid'] : undefined;
  const field = SOURCE_TEST_IDS[testId];
  const supported = field && children.type === 'input';
  const pending = supported && source.selection && source.field === field;
  const saved = supported && !source.selection && source.field === field && source.lastChange?.veld === field;
  const warning = pending ? sourceWarning(field, source.value) : '';
  const error = (pending || saved) ? source.error || warning : '';
  const describedBy = pending ? `${testId}-source-state` : undefined;
  const input = supported ? cloneElement(children, {
    // Use text for a pending date/amount too, so invalid OCR is visible rather than silently blanked by the browser.
    type: pending ? 'text' : children.props.type,
    value: pending ? source.value : children.props.value,
    disabled: children.props.disabled || (source.busy && source.field === field),
    'data-source-active': String(source.field === field),
    'data-source-pending': String(!!pending),
    'aria-describedby': describedBy,
    'aria-invalid': !!error,
    onChange: pending ? e => source.setValue(e.target.value) : children.props.onChange,
  }) : children;
  return <div className={`${span} min-w-0`}>
    <label className={LABEL}>{label}</label>
    {input}
    {pending && <div className="mt-1 space-y-1 text-[11px] normal-case" data-testid={`${testId}-source-actions`}>
      <span id={describedBy} className="block font-semibold text-[#002FA7]" role="status" data-testid={`${testId}-source-state`}>Voorstel uit document · nog niet opgeslagen</span>
      {source.selection.confidence !== null && source.selection.confidence < 0.9 && <span className="block text-amber-800" data-testid={`${testId}-source-confidence`}>Controle nodig · herkenning {Math.round(source.selection.confidence * 100)}%</span>}
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={source.confirm} disabled={source.busy || !!warning}
          className="inline-flex items-center gap-1 px-2 py-1.5 bg-[#002FA7] text-white hover:bg-[#002380] disabled:opacity-50 transition-colors" data-testid={`${testId}-source-confirm`}><Check size={13} />{source.busy ? 'Opslaan…' : 'Bevestigen'}</button>
        <button type="button" onClick={source.cancel} disabled={source.busy}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-50 transition-colors" data-testid={`${testId}-source-cancel`}><X size={13} />Annuleren</button>
      </div>
    </div>}
    {saved && <button type="button" onClick={() => source.undo(source.lastChange.id)} disabled={source.busy}
      className="inline-flex items-center gap-1 mt-1 text-[11px] text-[#002FA7] hover:underline disabled:opacity-50" data-testid={`${testId}-source-undo`}><ArrowCounterClockwise size={12} />Ongedaan maken</button>}
    {error && <p role="alert" className="mt-1 text-[11px] text-red-700 break-words" data-testid={`${testId}-source-error`}>{error}</p>}
  </div>;
};

export const IncomingDocumentSection = ({ icon: Icon, title, sub, kleur = '#002FA7', kKey, children, badge, testId, defaultOpen = true }) => {
  const { collapsed, setCollapsed } = useContext(IncomingDocumentContext);
  const open = kKey ? (collapsed[kKey] === undefined ? defaultOpen : !collapsed[kKey]) : true;
  return <div className="bg-white border border-slate-200" data-testid={testId}>
    <button type="button" className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 transition-colors" style={{ borderLeft: `3px solid ${kleur}` }}
      onClick={() => kKey && setCollapsed(p => ({ ...p, [kKey]: open }))} disabled={!kKey} aria-expanded={open} data-testid={`${testId}-toggle`}>
      <Icon size={13} weight="bold" style={{ color: kleur }} />
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-800">{title}</span>
      {badge}
      {sub && <span className="text-[11px] text-slate-500 truncate flex-1 min-w-0">{sub}</span>}
      {kKey && (open ? <CaretUp size={12} className="text-slate-400 ml-auto shrink-0" /> : <CaretDown size={12} className="text-slate-400 ml-auto shrink-0" />)}
    </button>
    {open && <div className="px-3 pb-3 pt-2 border-t border-slate-100">{children}</div>}
  </div>;
};